import { format, differenceInMinutes, differenceInSeconds } from "date-fns";
import { useState, useEffect } from "react";
import { X, Pencil } from "lucide-react";
import { api } from "../../lib/api";
import moment from "moment";
import Button from "./Button";

const safeDiff = (start, end) => {
    const diff = differenceInSeconds(new Date(end), new Date(start));
    return diff < 0 ? 0 : diff;
};

function LiveSession({ session }) {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        if (session.endTime) return;
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, [session]);

    const durationSec = session.durationMinutes
        ? session.durationMinutes * 60
        : session.endTime
            ? safeDiff(session.startTime, session.endTime)
            : safeDiff(session.startTime, now);

    const lastActive = session.endTime
        ? new Date(session.lastActive || session.endTime)
        : now;

    return (
        <>
            <td className="px-2 py-1 border hidden zx:table-cell">
                {formatSeconds(durationSec)}
            </td>
            <td className="px-2 py-1 border">
                {lastActive ? format(lastActive, "HH:mm:ss") : "-"}
            </td>
        </>
    );
}

// function mapTasksToSessions(tasks = [], sessions = []) {
//     if (!Array.isArray(tasks) || !tasks.length) {
//         console.log("No tasks available.");
//         return;
//     }
//     if (!Array.isArray(sessions) || !sessions.length) {
//         console.log("No sessions available.");
//         return;
//     }

//     // Sort sessions by start time
//     const sortedSessions = sessions
//         .filter(s => s.startTime)
//         .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

//     console.log("=== TASKS DIVIDED BY SESSION (IST) ===");

//     let totalSeconds = 0; // accumulator for total duration

//     sortedSessions.forEach((session, sIndex) => {
//         const sessionStart = new Date(session.startTime).getTime();
//         const sessionEnd = session.endTime ? new Date(session.endTime).getTime() : Date.now();

//         console.log(
//             `\nSession ${sIndex + 1}: Start - ${new Date(sessionStart).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}, ` +
//             `End - ${session.endTime ? new Date(sessionEnd).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "ONGOING"}`
//         );

//         tasks.forEach((task, tIndex) => {
//             (task.timeline || []).forEach((t, timelineIndex) => {
//                 const taskStart = new Date(t.startTime).getTime();
//                 const taskEnd = t.endTime ? new Date(t.endTime).getTime() : Date.now();

//                 const overlapStart = Math.max(taskStart, sessionStart);
//                 const overlapEnd = Math.min(taskEnd, sessionEnd);

//                 if (overlapStart < overlapEnd) {
//                     const durationSeconds = Math.floor((overlapEnd - overlapStart) / 1000);
//                     totalSeconds += durationSeconds; // add to total

//                     console.log(
//                         `  Task ${tIndex + 1} Timeline ${timelineIndex + 1}: Overlaps Session  | Duration: ${durationSeconds}s | ` +
//                         `Start: ${new Date(overlapStart).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}, ` +
//                         `End: ${new Date(overlapEnd).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`
//                     );
//                 }
//             });
//         });
//     });

//     const totalHours = (totalSeconds / 3600).toFixed(2);
//     console.log(`\nTotal Overlapping Time: ${totalHours} hours`);
// }

function calculateTotalTimeByDate(tasks = [], employeeId, workDate, sessions = []) {
    const dateMap = {};
    const activeMapByDate = {};
    const now = Date.now();

    if (!Array.isArray(tasks) || !tasks.length)
        return { dateMap, activeMapByDate, totalSeconds: 0 };

    if (!Array.isArray(sessions) || !sessions.length)
        return { dateMap, activeMapByDate, totalSeconds: 0 };

    const sortedSessions = sessions
        .filter(s => s.startTime)
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    let totalSeconds = 0;

    sortedSessions.forEach((session) => {

        const sessionStart = new Date(session.startTime).getTime();
        let sessionEndTime = session.endTime ? new Date(session.endTime).getTime() : now;

        if (sessionEndTime < sessionStart) {
            sessionEndTime += 24 * 60 * 60 * 1000;
        }

        const sessionEnd = sessionEndTime;
        tasks.forEach((task) => {

            (task.timeline || []).forEach((t) => {

                if (!t.startTime || t.employeeId !== employeeId) return;

                const taskStart = new Date(t.startTime).getTime();
                const taskEnd = t.endTime ? new Date(t.endTime).getTime() : now;

                const overlapStart = Math.max(taskStart, sessionStart);
                const overlapEnd = Math.min(taskEnd, sessionEnd);

                if (overlapStart >= overlapEnd) return;

                const durationSeconds = Math.floor((overlapEnd - overlapStart) / 1000);

                totalSeconds += durationSeconds;
            });

        });

    });

    // console.log("Total Overlapping Time:", formatSeconds(totalSeconds));
    return { dateMap, activeMapByDate, totalSeconds };
}

function TotalAttendance({ tasks, employeeId, workDate, sessions }) {
    const [seconds, setSeconds] = useState(0);

    useEffect(() => {
        const updateTime = () => {
            const filteredTasks = tasks.filter(task =>
                (task.timeline || []).some(t => t.employeeId === employeeId)
            );

            const { totalSeconds } =
                calculateTotalTimeByDate(filteredTasks, employeeId, workDate, sessions);

            setSeconds(totalSeconds);
        };

        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [tasks, employeeId, workDate, sessions]);

    const isWorkingNow = sessions?.some(session => !session.endTime);

    const isTaskRunning = tasks?.some(task =>
        (task.timeline || []).some(
            t => t.employeeId === employeeId && !t.endTime
        )
    );
    return (
        <span className="text-green-600 text-sm ml-2">
            <span className="text-[black]">Total Working Time:</span>{" "}
            {formatSeconds(seconds)}

            {isWorkingNow && isTaskRunning && seconds > 0 && (
                <span className="ml-1 text-xs px-1 py-0.5 rounded bg-yellow-100 text-yellow-700">
                    Working
                </span>
            )}
        </span>
    );
}

function LiveTotalLoginTime({ sessions }) {
    const [totalSeconds, setTotalSeconds] = useState(0);

    useEffect(() => {
        const calculateTotal = () => {
            const seconds = (sessions || []).reduce((sum, session) => {
                const durationSec = session.durationMinutes
                    ? Math.max(0, session.durationMinutes * 60)
                    : session.endTime
                        ? safeDiff(session.startTime, session.endTime)
                        : safeDiff(session.startTime, new Date());

                return sum + durationSec;
            }, 0);

            setTotalSeconds(seconds);
        };

        calculateTotal();
        const interval = setInterval(calculateTotal, 1000);

        return () => clearInterval(interval);
    }, [sessions]);

    return <>{formatSeconds(totalSeconds)}</>;
}

function formatSeconds(totalSeconds = 0) {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export default function AttendanceTable({
    entries,
    rangeType,
    startDate,
    endDate,
    selectedYear,
    holidaysData,
    leavesData,
    searchText,
    projects,
    totalemployee,
    loggedInUser,
    employeeStatusFilter
}) {

    const [selectedDetail, setSelectedDetail] = useState(null);
    const currentUserDepartment = loggedInUser?.department;
    const excludedDepartments = ["Admin"];
    const [selectedStatus, setSelectedStatus] = useState("");
    const [localEntries, setLocalEntries] = useState(entries);
    const [editOptions, setEditOptions] = useState(false);
    const [type, setType] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [fromHour, setFromHour] = useState("09");
    const [fromMinute, setFromMinute] = useState("00");
    const [fromPeriod, setFromPeriod] = useState("AM");
    const [toHour, setToHour] = useState("10");
    const [toMinute, setToMinute] = useState("00");
    const [toPeriod, setToPeriod] = useState("AM");
    const [halfType, setHalfType] = useState("");

    const employees = [
        ...new Set([
            ...entries
                .filter(e => !excludedDepartments.includes(e.department))
                .filter(e => {
                    const emp = totalemployee
                        ?.flatMap(dept => dept.employees || [])
                        .find(x => x.employee_id === e.userId);

                    const allowedStatuses = employeeStatusFilter === "All"
                        ? ["Active", "Trainee", "Terminated"]
                        : [employeeStatusFilter];

                    return emp && allowedStatuses.includes(emp.employee_status);
                })
                .map(e => e.userId),

            ...((currentUserDepartment === "HR" || currentUserDepartment === "Admin")
                ? totalemployee
                    ?.filter(dept => dept.departmentName === "Facilities")
                    .flatMap(dept => dept.employees || [])
                    .filter(emp => {
                        const allowedStatuses = employeeStatusFilter === "All"
                            ? ["Active", "Trainee", "Terminated"]
                            : [employeeStatusFilter];

                        const status = emp.employee_status || emp.employeeStatus || "";
                        return allowedStatuses.includes(status);
                    })
                    .map(emp => emp.employee_id)
                : [])
        ])
    ];

    const columns = [];
    if (rangeType === "yearly") {
        for (let m = 0; m < 12; m++) columns.push(m);
    } else {
        let d = new Date(startDate);
        while (d <= endDate) {
            columns.push(new Date(d));
            d.setDate(d.getDate() + 1);
        }
    }

    const siftingEmployee = totalemployee
        ? totalemployee.flatMap(dept => dept.employees || [])
            .filter(emp => emp.employee_id || (emp.workInformation && emp.workInformation.employeeShift))
        : [];

    const getWeekOfMonth = (date) => {
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const firstSaturdayOffset = (6 - firstDay.getDay() + 7) % 7;
        const firstSaturdayDate = 1 + firstSaturdayOffset;

        return Math.floor((date.getDate() - firstSaturdayDate) / 7) + 1;
    };

    const getStatus = (userId, col) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const date = new Date(col);
        date.setHours(0, 0, 0, 0);
        const day = date.getDay();

        const employee = siftingEmployee.find(emp => emp.employee_id === userId);
        const shift = employee?.workInformation?.employeeShift || "";

        const joiningDate = employee?.workInformation?.joiningDate
            ? new Date(employee.workInformation.joiningDate)
            : null;

        if (joiningDate) {
            joiningDate.setHours(0, 0, 0, 0);
            if (date < joiningDate) return "";
        }

        // Cutoff date: 1 July 2026
        const weekendOffCutoff = new Date(2026, 6, 1); // month is 0-indexed, so 6 = July
        weekendOffCutoff.setHours(0, 0, 0, 0);

        let isOff = false;

        if (date >= weekendOffCutoff) {
            // After 1 July 2026: Saturday & Sunday always off, no shift logic
            if (day === 6 || day === 0) isOff = true;
        } else {
            // Old logic before cutoff date
            if (day === 6) {
                const weekOfMonth = getWeekOfMonth(date);

                if (weekOfMonth >= 5) {
                    isOff = true;
                } else if (shift === "Day Shift") {
                    if (weekOfMonth === 1 || weekOfMonth === 3) isOff = true;
                } else if (shift === "Night Shift") {
                    if (weekOfMonth === 2 || weekOfMonth === 4) isOff = true;
                } else {
                    isOff = true;
                }
            }

            if (day === 0) isOff = true;
        }

        const holidaysArray = Array.isArray(holidaysData?.holidays) ? holidaysData.holidays : [];
        const isHoliday = holidaysArray.some(h => {
            if (h.type !== "holiday") return false;

            const startDate = new Date(h.startDate);
            const endDate = h.endDate ? new Date(h.endDate) : startDate;

            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            return date >= startDate && date <= endDate;
        });

        if (isOff) return "Off";
        if (isHoliday) return "HOLIDAY";

        const entry = localEntries.find(e =>
            e.userId === userId &&
            format(new Date(e.workDate), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
        );

        const departmentName = totalemployee
            ?.find(dept => dept._id === employee?.department_id)
            ?.departmentName;

        if (departmentName === "Facilities") {
            if (entry?.status) return entry.status;

            const createdAt = new Date(
                totalemployee
                    ?.find(dept => dept._id === employee?.department_id)
                    ?.createdAt
            );

            createdAt.setHours(0, 0, 0, 0);

            if (date >= createdAt && date <= today) {
                return "P";
            }
        }

        if (leavesData) {
            const leaveEntry = leavesData
                .find(u => u.userId === userId)
                ?.leaves?.find(leave => {
                    if (leave.status !== "Approved") return false;
                    const leaveStart = new Date(leave.startDate);
                    const leaveEnd = new Date(leave.endDate);
                    leaveStart.setHours(0, 0, 0, 0);
                    leaveEnd.setHours(0, 0, 0, 0);
                    return date >= leaveStart && date <= leaveEnd;
                });
            if (leaveEntry) {
                const type = leaveEntry.type?.trim().toLowerCase();
                const leaveMap = {
                    "half day": "P-HD",
                    "short leave": "P-SL",
                    "work from home": "P-WFH",
                    "casual leave": "CL",
                    "sick leave": "SL",
                    "personal": "PERS",
                    "urgent": "URG",
                    "maternity leave": "MAT",
                    "marriage leave": "MAR",
                    "paternity leave": "PAT",
                    "bereavement leave": "BERE"
                };
                return leaveMap[type] || "CL";
            }
        }

        const empMaster = totalemployee
            ?.flatMap(dept => dept.employees || [])
            .find(emp => emp.employee_id === userId);


        if (empMaster?.employee_status === "Terminated") {
            const rawDate = empMaster?.workInformation?.terminationDate || null;

            if (rawDate) {
                const leavingDate = new Date(rawDate);
                leavingDate.setHours(0, 0, 0, 0);
                if (date > leavingDate) {
                    return entry?.status || "";
                }
            }
        }

        if (!entry && date < today) return "A";
        if (!entry) return "";
        return entry.status || "P";

    };

    const getColor = (status) => {
        switch (status) {
            case "P": return "bg-green-400 text-white";
            case "A": return "bg-red-500 text-white";
            case "CL": return "bg-pink-300 text-white";
            case "SL": return "bg-purple-400 text-white";
            case "PERS": return "bg-teal-400 text-white";
            case "URG": return "bg-indigo-400 text-white";
            case "MAT": return "bg-pink-600 text-white";
            case "MAR": return "bg-yellow-400 text-black";
            case "PAT": return "bg-blue-400 text-white";
            case "BERE": return "bg-gray-700 text-white";
            case "P-SL": return "bg-yellow-300 text-black";
            case "P-WFH": return "bg-orange-400 text-white";
            case "P-HD": return "bg-[#93c47d] text-white";
            case "HOLIDAY": return "bg-[#ff9900] text-white";
            case "Off": return "bg-blue-600 text-white";
            default: return "bg-gray-100 text-gray-500";
        }
    };

    const handleCellClick = (userId, col) => {
        const date = new Date(col);
        date.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const status = getStatus(userId, col);

        const employee = totalemployee
            ?.flatMap(dept => dept.employees || [])
            .find(emp => emp.employee_id === userId);

        const departmentName = totalemployee
            ?.find(dept => dept._id === employee?.department_id)
            ?.departmentName;

        const hasEntry = localEntries.some(
            (entry) =>
                entry.userId === userId &&
                format(new Date(entry.workDate), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
        );

        const holidaysForDate = (holidaysData?.holidays || []).filter(h => {
            if (h.type !== "holiday") return false;

            const startDate = new Date(h.startDate);
            const endDate = h.endDate ? new Date(h.endDate) : startDate;

            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            return date >= startDate && date <= endDate;
        });

        if (date > today && holidaysForDate.length === 0) return;

        if (status === "Off") return;

        const entry = entries.find(e =>
            e.userId === userId &&
            format(new Date(e.workDate), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
        );

        if (departmentName === "Facilities") {
            setSelectedDetail({ userId, date, holidaysForDate, hasEntry: true, status: entry?.status || getStatus(userId, col) });
            setDefaultStatus(userId, col);
            return;
        }

        if (!hasEntry && holidaysForDate.length === 0) return;

        setSelectedDetail({ userId, date, holidaysForDate, hasEntry, status: entry?.status || getStatus(userId, col) });
    };

    const allTasks = [];
    projects.forEach(project => {
        if (!project.isMultiProject) allTasks.push(...Object.values(project.statusTask || {}).flat());
        else project.subProjects?.forEach(sub => allTasks.push(...Object.values(sub.statusTask || {}).flat()));
    });

    const groupedEntries = Object.values(
        entries.reduce((acc, entry) => {
            const key =
                entry.userId +
                "_" +
                format(new Date(entry.workDate), "yyyy-MM-dd");

            if (!acc[key]) {
                acc[key] = {
                    ...entry,
                    sessions: [...(entry.sessions || [])],
                };
            } else {
                acc[key].sessions.push(...(entry.sessions || []));
            }

            return acc;
        }, {})
    );

    const statusMap = {
        "present": "P",
        "absent": "A",
        "casual leave": "CL",
        "short leave": "P-SL",
        "half day": "P-HD"
    };

    const setDefaultStatus = (userId, col) => {
        const status = getStatus(userId, col);

        const reverseStatusMap = {
            "P": "Present",
            "A": "Absent",
            "CL": "casual leave",
            "P-SL": "short leave",
            "P-HD": "half day"
        };

        setSelectedStatus(reverseStatusMap[status] || "");
    };

    const currentStatus = statusMap[selectedStatus];

    const convertTo24Hour = (hour, minute, period) => {
        let h = parseInt(hour);

        if (period === "PM" && h !== 12) h += 12;
        if (period === "AM" && h === 12) h = 0;

        return `${String(h).padStart(2, "0")}:${minute}`;
    };

    const updateAttendance = async (value) => {
        setIsLoading(true);
        const statusCode = statusMap[value?.toLowerCase()];

        if (!statusCode || !selectedDetail) return;

        const userId = selectedDetail.userId;
        const date = new Date(selectedDetail.date).toLocaleDateString("en-CA");

        const userFromMaster = totalemployee
            ?.flatMap(dept => dept.employees || [])
            .find(emp => emp.employee_id === selectedDetail.userId);
        const isFacilities =
            totalemployee
                ?.find(d => d._id === userFromMaster?.department_id)
                ?.departmentName === "Facilities";

        let fromTime24 = "";
        let toTime24 = "";

        if (!isFacilities) {

            if (value === "short leave") {

                fromTime24 = convertTo24Hour(fromHour, fromMinute, fromPeriod);
                toTime24 = convertTo24Hour(toHour, toMinute, toPeriod);

                if (fromTime24 >= toTime24) {
                    alert("To Time must be greater than From Time");
                    return;
                }

                const from = moment(fromTime24, "HH:mm");
                const to = moment(toTime24, "HH:mm");

                const diffMinutes = to.diff(from, "minutes");

                if (diffMinutes > 120) {
                    alert("Short Leave cannot be more than 2 hours");
                    return;
                }
            }

            if (value === "half day" && !halfType) {
                alert("Please select First Half or Second Half");
                return;
            }

        }

        const payload = {
            userId: selectedDetail?.userId || null,
            date: selectedDetail?.date || null,
            status: statusCode || null,
            username: userFromMaster?.office_email || null,
            fullname: userFromMaster
                ? `${userFromMaster.first_name || ""} ${userFromMaster.last_name || ""}`
                : null,
            department: totalemployee
                ?.find(d => d._id === userFromMaster?.department_id)
                ?.departmentName || null,
            leaveStatus: "Approved",
            type: value || null,

            halfType: value === "half day" ? halfType : null,

            fromTime:
                value === "short leave"
                    ? convertTo24Hour(fromHour, fromMinute, fromPeriod)
                    : null,

            toTime:
                value === "short leave"
                    ? convertTo24Hour(toHour, toMinute, toPeriod)
                    : null
        };

        try {
            await fetch(api.auth.attendencePost.path, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(payload)
            });

            setLocalEntries(prev => {
                const exists = prev.find(
                    e =>
                        e.userId === userId &&
                        format(new Date(e.workDate), "yyyy-MM-dd") === date
                );

                if (exists) {
                    return prev.map(e =>
                        e.userId === userId &&
                            format(new Date(e.workDate), "yyyy-MM-dd") === date
                            ? { ...e, status: statusCode }
                            : e
                    );
                } else {
                    return [
                        ...prev,
                        {
                            userId,
                            workDate: date,
                            status: statusCode
                        }
                    ];
                }
            });
            setSelectedDetail(prev => ({
                ...prev,
                status: statusCode
            }));

        } catch (error) {
            console.error("Error updating attendance:", error);
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="overflow-x-auto border rounded-lg shadow-md">
            <table className="divide-y divide-gray-200 text-center">
                <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                        <th className="px-2 sm:px-4 py-1 sm:py-2 text-[12px] sm:text-[12px] text-left text-gray-700 font-semibold sticky left-0 bg-gray-50 z-20">
                            EMP
                        </th>
                        {columns.map(col => (
                            <th
                                key={
                                    rangeType === "yearly"
                                        ? `month-${col}`
                                        : `date-${format(new Date(col), "yyyy-MM-dd")}`
                                }
                                className="w-[45px] h-[40px] px-1 text-[10px] sm:text-[12px] py-0 text-gray-700 font-semibold text-center align-middle"
                            >
                                {rangeType === "yearly"
                                    ? format(new Date(selectedYear, col, 1), "MMM")
                                    : format(col, "EEE dd")}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {employees
                        .filter(userId => {
                            const userFromEntries = entries.find(e => e.userId === userId);
                            const userFromMaster = totalemployee
                                ?.flatMap(dept => dept.employees || [])
                                .find(emp => emp.employee_id === userId);

                            const user = userFromEntries || {
                                userId: userFromMaster?.employee_id,
                                fullname: `${userFromMaster?.first_name || ""} ${userFromMaster?.last_name || ""}`,
                                department: "Facilities"
                            };
                            if (!user) return false;
                            const search = searchText?.toLowerCase() || "";
                            if (currentUserDepartment === "Management" && user.department === "HR") {
                                return false;
                            }
                            return (
                                user.userId?.toLowerCase().includes(search) ||
                                user.fullname?.toLowerCase().includes(search)
                            );
                        })
                        .map(userId => {
                            const userFromEntries = entries.find(e => e.userId === userId);
                            const userFromMaster = totalemployee
                                ?.flatMap(dept => dept.employees || [])
                                .find(emp => emp.employee_id === userId);

                            const user = userFromEntries || {
                                userId: userFromMaster?.employee_id,
                                fullname: `${userFromMaster?.first_name || ""} ${userFromMaster?.last_name || ""}`,
                                department: "Facilities"
                            };

                            return (
                                <tr
                                    key={userId}
                                    className="hover:bg-gray-50 transition-colors duration-150"
                                >
                                    <td className="px-2 sm:px-4 py-1 sm:py-2 text-left text-[10px] sm:text-[12px] sticky left-0 bg-white z-10 border-r border-gray-200 w-auto">
                                        {user?.userId ? `${user.userId} - ` : ""}
                                        {user?.fullname || "Unknown"}
                                    </td>
                                    {columns.map(col => (
                                        <td
                                            key={
                                                rangeType === "yearly"
                                                    ? `cell-${userId}-${col}`
                                                    : `cell-${userId}-${format(new Date(col), "yyyy-MM-dd")}`
                                            }
                                            className={`w-[45px] h-[40px] px-1 text-[10px] cursor-pointer text-center align-middle ${getColor(
                                                getStatus(userId, col)
                                            )} border border-gray-200`}
                                            onClick={() => handleCellClick(userId, col)}
                                        >
                                            {getStatus(userId, col)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                </tbody>
            </table>

            {selectedDetail && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                    onClick={() => {
                        setSelectedDetail(null);
                        setEditOptions(false);
                    }}
                >
                    <div
                        className="bg-white relative animate-scaleIn rounded-lg w-full max-w-[700px] shadow-lg h-[85vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 p-2 sm:px-4 py-1 sm:py-3">
                            <h2 className="text-white text-[12px] sm:text-sm">Employee Details</h2>

                            <button
                                onClick={() => setSelectedDetail(null)}
                                className="text-white hover:text-red-400"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-2 sm:p-4 flex-1 overflow-auto">

                            {(() => {
                                const filteredEntries = groupedEntries.filter(
                                    (entry) =>
                                        entry.userId === selectedDetail.userId &&
                                        new Date(entry.workDate).toDateString() ===
                                        new Date(selectedDetail.date).toDateString()
                                );

                                const userFromMaster = totalemployee
                                    ?.flatMap(dept => dept.employees || [])
                                    .find(emp => emp.employee_id === selectedDetail.userId);

                                const departmentName = totalemployee
                                    ?.find(d => d._id === userFromMaster?.department_id)
                                    ?.departmentName;

                                const status = getStatus(
                                    selectedDetail.userId,
                                    selectedDetail.date
                                );

                                if (
                                    departmentName === "Facilities" &&
                                    !(selectedDetail?.holidaysForDate?.length > 0)
                                ) {
                                    return (
                                        <div>
                                            <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-3">
                                                <p className="font-semibold">
                                                    {userFromMaster.employee_id} - {userFromMaster.first_name} {userFromMaster.last_name}
                                                </p>

                                                <p className="text-sm text-gray-600 sm:text-right">
                                                    Date: {format(new Date(selectedDetail.date), "dd-MM-yyyy")}
                                                </p>
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                <span className="capitalize bg-yellow-500 text-white px-2 py-1 rounded-md text-xs">
                                                    {departmentName}
                                                </span>

                                                <div className="flex gap-2 items-center">
                                                    <span className={`px-2 py-1 rounded text-xs ${getColor(status)}`}>
                                                        {status}
                                                    </span><Pencil size={14} color="orange" cursor="pointer" onClick={() => setEditOptions(true)} />
                                                </div>
                                            </div>
                                            {editOptions && (currentUserDepartment === "HR" || currentUserDepartment === "Admin") && (
                                                <div className="mt-3 flex justify-end">
                                                    <select
                                                        value={selectedStatus}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setSelectedStatus(value);
                                                            updateAttendance(value);
                                                        }}
                                                        className="w-full sm:w-auto border rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    >
                                                        <option value="">Select an option</option>
                                                        <option value="short leave">Short Leave</option>
                                                        <option value="half day">Half Day</option>
                                                        <option value="Present">Present</option>
                                                        <option value="Absent">Absent</option>
                                                        <option value="casual leave">Causal Leave</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                if (filteredEntries.length > 0) {
                                    return filteredEntries.map((entry) => (
                                        <div key={`${entry.userId}-${new Date(entry.workDate).toDateString()}`}>
                                            {/* HEADER */}
                                            <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-3 text-[12px] sm:text-[14px]">

                                                {/* LEFT */}
                                                <div>
                                                    <p className="font-semibold mb-2">
                                                        {entry?.userId ? `${entry.userId} - ` : ""}
                                                        {entry.fullname}
                                                    </p>

                                                    <div className="flex flex-wrap gap-2">
                                                        <span className="capitalize bg-yellow-500 text-white px-2 py-1 rounded-md text-xs">
                                                            {entry.department}
                                                        </span>

                                                        <div className="flex gap-2 items-center">
                                                            <span className={`px-2 py-1 rounded text-xs ${getColor(status)}`}>
                                                                {status}
                                                            </span>
                                                            {(currentUserDepartment === "HR" || currentUserDepartment === "Admin") && (
                                                                <Pencil
                                                                    size={14}
                                                                    color="orange"
                                                                    cursor="pointer"
                                                                    onClick={() => setEditOptions(true)}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>


                                                {/* RIGHT */}
                                                <div className="text-left sm:text-right space-y-1">
                                                    <p className="font-medium">
                                                        Date: {format(new Date(entry.workDate), "dd-MM-yyyy")}
                                                    </p>

                                                    {status !== "A" && status !== "CL" && (<div>
                                                        <p className="text-gray-600">
                                                            Total Login Time:{" "}
                                                            <LiveTotalLoginTime sessions={entry.sessions} />
                                                        </p>

                                                        {entry.department !== "Sales" &&
                                                            entry.department !== "Management" &&
                                                            entry.department !== "HR" && (
                                                                <TotalAttendance
                                                                    tasks={allTasks}
                                                                    employeeId={entry.userId}
                                                                    workDate={new Date(entry.workDate)}
                                                                    sessions={entry.sessions}
                                                                />
                                                            )}
                                                    </div>)}
                                                </div>
                                            </div>
                                            {editOptions && (currentUserDepartment === "HR" || currentUserDepartment === "Admin") && (
                                                <div className="mt-3 flex flex-col items-end w-full">

                                                    {/* STATUS SELECT */}
                                                    <select
                                                        value={selectedStatus}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setSelectedStatus(value);
                                                            setType(value);
                                                        }}
                                                        className="w-full sm:w-[220px] border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    >
                                                        <option value="">Select an option</option>
                                                        <option value="short leave">Short Leave</option>
                                                        <option value="half day">Half Day</option>
                                                        <option value="Present">Present</option>
                                                        <option value="Absent">Absent</option>
                                                        <option value="casual leave">Causal Leave</option>
                                                    </select>

                                                    {/* SHORT LEAVE */}
                                                    {type === "short leave" && (
                                                        <div className="flex flex-col sm:flex-row gap-4 mt-4 w-full">

                                                            {/* FROM TIME */}
                                                            <div className="flex flex-col w-full">
                                                                <label className="text-[12px] sm:text-sm font-medium mb-1 text-gray-700">
                                                                    From Time
                                                                </label>

                                                                <div className="flex gap-2">
                                                                    <select
                                                                        value={fromHour}
                                                                        onChange={(e) => setFromHour(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                                                                    >
                                                                        {[...Array(12)].map((_, i) => {
                                                                            const h = String(i + 1).padStart(2, "0");
                                                                            return <option key={h}>{h}</option>;
                                                                        })}
                                                                    </select>

                                                                    <select
                                                                        value={fromMinute}
                                                                        onChange={(e) => setFromMinute(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                                                                    >
                                                                        {["00", "15", "30", "45"].map((m) => (
                                                                            <option key={m}>{m}</option>
                                                                        ))}
                                                                    </select>

                                                                    <select
                                                                        value={fromPeriod}
                                                                        onChange={(e) => setFromPeriod(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                                                                    >
                                                                        <option>AM</option>
                                                                        <option>PM</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            {/* TO TIME */}
                                                            <div className="flex flex-col w-full">
                                                                <label className="text-[12px] sm:text-sm font-medium mb-1 text-gray-700">
                                                                    To Time
                                                                </label>

                                                                <div className="flex gap-2">
                                                                    <select
                                                                        value={toHour}
                                                                        onChange={(e) => setToHour(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                                                                    >
                                                                        {[...Array(12)].map((_, i) => {
                                                                            const h = String(i + 1).padStart(2, "0");
                                                                            return <option key={h}>{h}</option>;
                                                                        })}
                                                                    </select>

                                                                    <select
                                                                        value={toMinute}
                                                                        onChange={(e) => setToMinute(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                                                                    >
                                                                        {["00", "15", "30", "45"].map((m) => (
                                                                            <option key={m}>{m}</option>
                                                                        ))}
                                                                    </select>

                                                                    <select
                                                                        value={toPeriod}
                                                                        onChange={(e) => setToPeriod(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                                                                    >
                                                                        <option>AM</option>
                                                                        <option>PM</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    )}

                                                    {/* HALF DAY */}
                                                    {type === "half day" && (
                                                        <div className="mt-4 w-full">
                                                            <label className="block text-[12px] sm:text-sm font-medium mb-1 text-gray-700">
                                                                Half Day Type
                                                            </label>

                                                            <select
                                                                value={halfType}
                                                                onChange={(e) => setHalfType(e.target.value)}
                                                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                            >
                                                                <option value="">Select Half</option>
                                                                <option value="First Half">First Half (Morning)</option>
                                                                <option value="Second Half">Second Half (Afternoon)</option>
                                                            </select>
                                                        </div>
                                                    )}

                                                    {/* SAVE BUTTON */}
                                                    <div className="mt-4 w-full flex justify-end">
                                                        <Button
                                                            variant="success"
                                                            className="px-5 py-2 text-sm flex items-center justify-center gap-2"
                                                            disabled={!selectedStatus || isLoading}
                                                            onClick={() => updateAttendance(selectedStatus)}
                                                        >
                                                            {isLoading && (
                                                                <span className="loader w-4 h-4 border-2 border-white"></span>
                                                            )}
                                                            {isLoading ? "Saving..." : "Save"}
                                                        </Button>
                                                    </div>

                                                </div>)}



                                            {/* TABLE */}
                                            {status !== "A" && status !== "CL" && (
                                                <div className="overflow-x-auto mt-3">
                                                    <table className="w-full text-[12px] sm:text-[14px] border border-gray-200 rounded-md overflow-hidden">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="px-3 py-2 border">#</th>
                                                                <th className="px-3 py-2 border">Login Time</th>
                                                                <th className="px-3 py-2 border">Logout Time</th>
                                                                <th className="px-3 py-2 border hidden zx:table-cell">
                                                                    Duration
                                                                </th>
                                                                <th className="px-3 py-2 border">Last Active</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {(entry.sessions || []).map((session, index) => (
                                                                <tr key={session._id || index} className="text-center border-t hover:bg-gray-50">
                                                                    <td className="px-2 py-1 border">{index + 1}</td>

                                                                    <td className="px-2 py-1 border">
                                                                        {session.startTime
                                                                            ? format(new Date(session.startTime), "HH:mm:ss")
                                                                            : "-"}
                                                                    </td>

                                                                    <td className="px-2 py-1 border">
                                                                        {session.endTime
                                                                            ? format(new Date(session.endTime), "HH:mm:ss")
                                                                            : "-"}
                                                                    </td>

                                                                    <LiveSession session={session} />
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>)}
                                        </div>
                                    ));
                                }

                                return <div>No data</div>;
                            })()}

                            {selectedDetail?.holidaysForDate?.length > 0 && (
                                <div className="my-4">
                                    <h3 className="font-semibold mb-2">Holidays on this date:</h3>
                                    <ul className="list-disc list-inside text-sm">
                                        {selectedDetail.holidaysForDate.map(h => {
                                            const start = new Date(h.startDate).toLocaleDateString();
                                            const end = h.endDate
                                                ? new Date(h.endDate).toLocaleDateString()
                                                : start;

                                            return (
                                                <li key={h._id}>
                                                    <span className="font-medium">{h.title}</span> ({h.type})
                                                    <span className="text-gray-500 text-xs ml-1">
                                                        {start !== end ? `${start} → ${end}` : start}
                                                    </span>
                                                    {h.description && (
                                                        <div
                                                            className="text-gray-600 text-xs mt-1"
                                                            dangerouslySetInnerHTML={{ __html: h.description }}
                                                        />
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
